import {agentTaskQueryKey, fetchTasksBatch} from '@github-ui/agent-sessions/hooks/use-task'
import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import {isSessionState, TERMINAL_SESSION_STATES} from '@github-ui/agent-sessions/types/session'
import {getAPITaskState} from '@github-ui/agent-sessions/utils/task-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type QueryClient, queryOptions, skipToken, useQuery, useQueryClient} from '@github-ui/react-query'
import {createContext, type ReactNode, use, useEffect, useMemo, useState, useSyncExternalStore} from 'react'

import type {BoardPayloadAgentTask} from '../board-payload'

const POLL_INTERVAL_MS = 30_000
const STATUS_QUERY_KEY = 'board-agent-session-status'
const STATUS_FLAG = 'planning_board_agent_task_status'
// The shared client turns retries off.
const STATUS_RETRIES = 2

type TaskLocation = {id: string; owner: string; repo: string; repoNwo: string}

const NO_IDS: readonly string[] = []

/** Task ids on mounted cards, and which of them the board has asked Mission Control about. */
class AgentTaskRegistry {
  #locations = new Map<string, TaskLocation>()
  #mounted = new Map<string, number>()
  #requested = new Set<string>()
  #failed = new Set<string>()
  #unanswered = new Set<string>()
  #unrequested: readonly string[] = NO_IDS
  #listeners = new Set<() => void>()

  /** Whether the board asks Mission Control at all. */
  readonly enabled: boolean

  constructor(enabled: boolean) {
    this.enabled = enabled
  }

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener)
    return () => {
      this.#listeners.delete(listener)
    }
  }

  /** Counts a mounted card holding the task, and returns its release. */
  mount(location: TaskLocation): () => void {
    this.#locations.set(location.id, location)
    this.#mounted.set(location.id, (this.#mounted.get(location.id) ?? 0) + 1)
    this.#updateUnrequested()
    return () => {
      const count = (this.#mounted.get(location.id) ?? 1) - 1
      if (count > 0) this.#mounted.set(location.id, count)
      else this.#mounted.delete(location.id)
      this.#updateUnrequested()
    }
  }

  /** Sorted, so the same set is the same snapshot. */
  getUnrequested = () => this.#unrequested

  mountedLocations(): TaskLocation[] {
    return [...this.#mounted.keys()].flatMap(id => this.#locations.get(id) ?? [])
  }

  location(id: string) {
    return this.#locations.get(id)
  }

  isFailed(id: string) {
    return this.#failed.has(id)
  }

  /** Whether the last batch asking about the task failed after its retries. */
  isUnanswered(id: string) {
    return this.#unanswered.has(id)
  }

  markUnanswered(ids: readonly string[]) {
    const before = this.#unanswered.size
    for (const id of ids) this.#unanswered.add(id)
    if (this.#unanswered.size !== before) this.#emit()
  }

  /** Asks about a task again on its next mount, unless it was missing. */
  forget(id: string) {
    if (!this.#failed.has(id)) this.#requested.delete(id)
  }

  /** Ids left out of an answer are missing. */
  settle(requested: readonly string[], found: ReadonlySet<string>) {
    let changed = false
    for (const id of requested) {
      this.#requested.add(id)
      if (this.#unanswered.delete(id)) changed = true
      const failed = !found.has(id)
      if (failed === this.#failed.has(id)) continue
      if (failed) this.#failed.add(id)
      else this.#failed.delete(id)
      changed = true
    }
    if (!this.#updateUnrequested() && changed) this.#emit()
  }

  #updateUnrequested() {
    const next = [...this.#mounted.keys()].filter(id => !this.#requested.has(id)).sort()
    if (next.join() === this.#unrequested.join()) return false
    this.#unrequested = next.length > 0 ? next : NO_IDS
    this.#emit()
    return true
  }

  #emit() {
    for (const listener of this.#listeners) listener()
  }
}

const AgentTaskRegistryContext = createContext<AgentTaskRegistry | null>(null)

function useRegistry() {
  const registry = use(AgentTaskRegistryContext)
  if (!registry) throw new Error('Agent session pills must render inside AgentSessionStatusProvider')
  return registry
}

function isRunning(task: HydratedAPITask) {
  const state = getAPITaskState(task)
  return !(isSessionState(state) && TERMINAL_SESSION_STATES.has(state))
}

async function fetchStatuses(
  queryClient: QueryClient,
  registry: AgentTaskRegistry,
  ids: readonly string[],
  signal: AbortSignal,
) {
  const tasks = await fetchTasksBatch({taskIds: [...ids], signal})
  const found = new Set<string>()
  for (const task of tasks) {
    const location = registry.location(task.id)
    if (!location) continue
    found.add(task.id)
    // Keyed as the panel's `useTask` reads it.
    queryClient.setQueryData(agentTaskQueryKey(location.owner, location.repo, task.id), task)
  }
  registry.settle(ids, found)
}

/**
 * Asks Mission Control about each mounted card's session once, in batches, then polls those still
 * running or left unanswered. Without the client flag nothing is requested.
 */
export function AgentSessionStatusProvider({children}: {children: ReactNode}) {
  const [registry] = useState(() => new AgentTaskRegistry(isFeatureEnabled(STATUS_FLAG)))
  const {enabled} = registry
  const unrequested = useSyncExternalStore(registry.subscribe, registry.getUnrequested, () => NO_IDS)
  const queryClient = useQueryClient()

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema, @tanstack/query/exhaustive-deps -- live status, no generated schema, and stable registry
  const firstAnswers = useQuery({
    queryKey: [STATUS_QUERY_KEY, 'new', unrequested],
    queryFn: async ({signal}) => {
      await fetchStatuses(queryClient, registry, unrequested, signal)
      return null
    },
    enabled: enabled && unrequested.length > 0,
    staleTime: Infinity,
    gcTime: 0,
    retry: STATUS_RETRIES,
  })
  // A failed batch is asked about again by the poll, or sooner once the set of unasked sessions changes.
  const unanswered = firstAnswers.isError ? unrequested : NO_IDS
  useEffect(() => {
    registry.markUnanswered(unanswered)
  }, [registry, unanswered])

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema, @tanstack/query/exhaustive-deps -- live status, no generated schema, and stable registry
  useQuery({
    queryKey: [STATUS_QUERY_KEY, 'poll'],
    queryFn: async ({signal}) => {
      const due = registry
        .mountedLocations()
        .filter(location => {
          if (registry.isUnanswered(location.id)) return true
          const task = queryClient.getQueryData<HydratedAPITask>(
            agentTaskQueryKey(location.owner, location.repo, location.id),
          )
          return task && isRunning(task)
        })
        .map(location => location.id)

      if (due.length > 0) await fetchStatuses(queryClient, registry, due, signal)
      return null
    },
    enabled,
    // The batch above gives the first answers.
    initialData: null,
    staleTime: POLL_INTERVAL_MS,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: false,
  })

  return <AgentTaskRegistryContext value={registry}>{children}</AgentTaskRegistryContext>
}

function toLocation(id: string | null, repoNwo: string): TaskLocation | null {
  const [owner, repo] = repoNwo.split('/')
  return id && owner && repo ? {id, owner, repo, repoNwo} : null
}

function cachedTaskQuery(location: TaskLocation | null) {
  return queryOptions<HydratedAPITask>({
    queryKey: location
      ? agentTaskQueryKey(location.owner, location.repo, location.id)
      : ['board-agent-session-without-id'],
    // Cache-only: the provider's batch fills it.
    queryFn: skipToken,
  })
}

export type AgentSessionStatus =
  | {kind: 'queued'}
  /** Being asked about. */
  | {kind: 'loading'}
  /** Won't be answered: the client flag is off, or the batch failed. */
  | {kind: 'unavailable'}
  /** Mission Control has no task with this id. */
  | {kind: 'missing'}
  | {kind: 'loaded'; task: HydratedAPITask; state: string}

/** One session's status from the provider's cache. Registers the task while mounted. */
export function useAgentSessionStatus(agentTask: BoardPayloadAgentTask): AgentSessionStatus {
  const registry = useRegistry()
  const queryClient = useQueryClient()
  const location = useMemo(() => toLocation(agentTask.id, agentTask.repoNwo), [agentTask.id, agentTask.repoNwo])

  useEffect(() => {
    if (!location) return
    // The cache drops a task once no card shows it, sooner than the registry forgets asking.
    if (!queryClient.getQueryData(cachedTaskQuery(location).queryKey)) registry.forget(location.id)
    return registry.mount(location)
  }, [registry, queryClient, location])

  const {data: task} = useQuery(cachedTaskQuery(location))
  const missing = useSyncExternalStore(
    registry.subscribe,
    () => !!location && registry.isFailed(location.id),
    () => false,
  )
  const unanswered = useSyncExternalStore(
    registry.subscribe,
    () => !!location && registry.isUnanswered(location.id),
    () => false,
  )

  if (!location) return {kind: 'queued'}
  // Checked first, so a task a later answer leaves out is dropped even with cached data.
  if (missing) return {kind: 'missing'}
  if (task) return {kind: 'loaded', task, state: getAPITaskState(task)}
  if (unanswered || !registry.enabled) return {kind: 'unavailable'}
  return {kind: 'loading'}
}
